import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import InilineColorPicker from '../../InlineColorPicker'
import ColorSchemesDropDown from './ColorSchemesDropDown'
import { Row, Col } from 'react-bootstrap'
import { ResetBtn, InvertBtn, LockBtn } from './ColorScaleUtils'
import get from 'lodash/get'
import keyBy from 'lodash/keyBy'
import {
  getInitialScaleValues,
  getColorScale,
  getDefaultColorScale,
  getColorDomain,
  colorPresets,
  getAvailableScaleTypes,
  getValueType,
} from '@rawgraphs/rawgraphs-core'
import styles from '../ChartOptions.module.scss'
import usePrevious from '../../../hooks/usePrevious'
import { getCompatibleInterpolators } from '../../../services/colorScaleOptions'

const SCALE_MODE_META = {
  ordinal: {
    label: 'Categories',
    description: 'Each category gets its own distinct color.',
  },
  sequential: {
    label: 'Low to high',
    description: 'A smooth gradient across a range of values.',
  },
  diverging: {
    label: 'Around a midpoint',
    description: 'Two directions from a shared center value.',
  },
}

function getDatePickerValue(userValue) {
  if (userValue.userDomain === 0) {
    return 0
  }
  if (!userValue.userDomain) {
    return ''
  }

  if (getValueType(userValue.userDomain) === 'date') {
    return userValue.userDomain.toISOString().substring(0, 10)
  }

  return userValue.userDomain
}

const ChartOptionColorScale = ({
  value,
  error,
  onChange,
  defaultValue,
  label,
  dimension,
  dataset,
  mapping,
  dataTypes,
  chart,
  mappedData,
  mappingValue,
  colorDataset,
  colorDataType,
  hasAnyMapping,
  ...props
}) => {

  // here we leverage injection of the __loaded prop in the color scale, see App.js
  const initialValue = useRef(!!value?.__loaded)
  
  const [scaleType, setScaleType] = useState(get(value, 'scaleType'))
  const [defaultOpacity, setDefaultOpacity] = useState(
    Number.isFinite(Number(get(value, 'defaultOpacity')))
      ? Number(get(value, 'defaultOpacity'))
      : 1
  )

  const defaultColor = useMemo(() => {
    const colorFromDefault = get(defaultValue, 'defaultColor', '#cccccc')
    return get(value, 'defaultColor', colorFromDefault)
  }, [defaultValue, value])

  const [locked, setLocked] = useState(get(value, 'locked'))

  const availableScaleTypes = useMemo(() => {
    const nextTypes = getAvailableScaleTypes(colorDataType, colorDataset)
    return nextTypes
  }, [colorDataType, colorDataset])

  const [interpolators, setInterpolators] = useState(
    getCompatibleInterpolators(
      get(value, 'scaleType'),
      colorDataset,
      colorDataType
    )
  )

  const [interpolator, setInterpolator] = useState(get(value, 'interpolator'))
  const [userValues, setUserValues] = useState(
    get(value, 'userScaleValues', []).map((userValue) => ({
      ...userValue,
      userDomain: userValue.domain,
      userRange: userValue.range,
      userOpacity:
        Number.isFinite(Number(userValue.opacity)) ? Number(userValue.opacity) : undefined,
      }))
  )
  const [isMobileLayout, setIsMobileLayout] = useState(false)
  const [showAllCategories, setShowAllCategories] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)')
    const handleChange = () => setIsMobileLayout(mediaQuery.matches)
    handleChange()

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }

    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [])

  useEffect(() => {
    const nextScaleType = get(value, 'scaleType')
    const nextInterpolator = get(value, 'interpolator')
    const nextLocked = get(value, 'locked')
    const nextDefaultOpacity = Number.isFinite(Number(get(value, 'defaultOpacity')))
      ? Number(get(value, 'defaultOpacity'))
      : 1
    const nextUserValues = get(value, 'userScaleValues', []).map((userValue) => ({
      ...userValue,
      userDomain: userValue.domain,
      userRange: userValue.range,
      userOpacity:
        Number.isFinite(Number(userValue.opacity)) ? Number(userValue.opacity) : undefined,
    }))

    setScaleType(nextScaleType)
    setInterpolator(nextInterpolator)
    setLocked(nextLocked)
    setDefaultOpacity(nextDefaultOpacity)
    setUserValues(nextUserValues)

    if (value?.__loaded || nextUserValues.length > 0) {
      initialValue.current = true
    }
  }, [value])

  useEffect(() => {
    setShowAllCategories(false)
  }, [mappingValue, scaleType, colorDataset])

  const getCurrentFinalScale = useCallback(
    (interpolator, scaleType, userValuesForFinalScale, defaultColor) => {
      if (
        !scaleType ||
        !interpolator ||
        !colorPresets[scaleType][interpolator] ||
        !userValuesForFinalScale ||
        !userValuesForFinalScale.length
      ) {
        return
      }

      const domains = userValuesForFinalScale
        .map((x) => x.domain)
        .filter((x) => x !== undefined)
      if (!domains.length) {
        return
      }

      const previewScale = getColorScale(
        colorDataset, //the array of values of the dataset mapped on the color dimension
        colorDataType,
        scaleType, //
        interpolator,
        userValuesForFinalScale,
      )

      return previewScale
    },
    [colorDataType, colorDataset]
  )

  const getDefaultUserValues = useCallback(
    (interpolator, scaleType) => {
      if (!colorDataset.length || !colorDataType || !scaleType) {
        return []
      }
      if (!colorPresets[scaleType][interpolator]) {
        return []
      }

      const domain = getColorDomain(colorDataset, colorDataType, scaleType)

      return getInitialScaleValues(domain, scaleType, interpolator).map(
        (userValue) => ({
          ...userValue,
          userRange: userValue.range,
          userDomain: userValue.domain,
        })
      )
    },
    [colorDataType, colorDataset]
  )

  const getUserValuesForFinalScale = useCallback(
    (values) => {
      return values.map((value) => ({
        range: value.userRange,
        domain:
          colorDataType === 'date'
            ? value.userDomain?.toString()
            : value.userDomain,
        opacity: Number.isFinite(Number(value.userOpacity))
          ? Number(value.userOpacity)
          : undefined,
        // domain: value.userDomain,
      }))
    },
    [colorDataType]
  )

  const currentFinalScale = useMemo(() => {

    if (scaleType && interpolator) {
      const currentUserValues =
        userValues && userValues.length
          ? userValues
          : getDefaultUserValues(interpolator, scaleType)
      const valuesForFinalScale = getUserValuesForFinalScale(currentUserValues)
      return getCurrentFinalScale(interpolator, scaleType, valuesForFinalScale)
    }
    return getDefaultColorScale()
  }, [
    getCurrentFinalScale,
    getDefaultUserValues,
    getUserValuesForFinalScale,
    interpolator,
    scaleType,
    userValues,
  ])

  const handleChangeValues = useCallback(
    (nextUserValues) => {
      let valuesForFinalScale = getUserValuesForFinalScale(nextUserValues)

      //notify ui
      const outScaleParams = {
        scaleType,
        interpolator: interpolator,
        userScaleValues: valuesForFinalScale,
        defaultOpacity,
        defaultColor,
        locked,
        __loaded: true,
      }
      onChange(outScaleParams)
    },
    [getUserValuesForFinalScale, scaleType, interpolator, defaultOpacity, defaultColor, locked, onChange]
  )

  const setUserValueRange = useCallback(
    (index, value) => {
      const newUserValues = [...userValues]
      newUserValues[index].userRange = value
      setUserValues(newUserValues)
      handleChangeValues(newUserValues)
    },
    [handleChangeValues, userValues]
  )

  const setUserValueDomain = useCallback(
    (index, value) => {
      const newUserValues = [...userValues]
      newUserValues[index].userDomain = value
      setUserValues(newUserValues)
      handleChangeValues(newUserValues)
    },
    [handleChangeValues, userValues]
  )

  const setUserValueOpacity = useCallback(
    (index, value) => {
      const newUserValues = [...userValues]
      newUserValues[index].userOpacity = Math.max(
        0.1,
        Math.min(1, Number(value) || 1)
      )
      setUserValues(newUserValues)
      handleChangeValues(newUserValues)
    },
    [handleChangeValues, userValues]
  )

  const clearUserValueOpacityOverride = useCallback(
    (index) => {
      const newUserValues = [...userValues]
      newUserValues[index].userOpacity = undefined
      setUserValues(newUserValues)
      handleChangeValues(newUserValues)
    },
    [handleChangeValues, userValues]
  )

  const handleChangeDefaultOpacity = useCallback(
    (nextDefaultOpacity) => {
      const normalizedOpacity = Math.max(
        0.1,
        Math.min(1, Number(nextDefaultOpacity) || 1)
      )
      setDefaultOpacity(normalizedOpacity)

      const valuesForFinalScale = getUserValuesForFinalScale(userValues)
      onChange({
        scaleType,
        interpolator,
        userScaleValues: valuesForFinalScale,
        defaultOpacity: normalizedOpacity,
        defaultColor,
        locked,
        __loaded: true,
      })
    },
    [
      defaultColor,
      getUserValuesForFinalScale,
      interpolator,
      locked,
      onChange,
      scaleType,
      userValues,
    ]
  )


  const handleChangeScaleType = useCallback(
    (nextScaleType) => {

      setScaleType(nextScaleType)

      //update interpolators
      const nextInterpolators = getCompatibleInterpolators(
        nextScaleType,
        colorDataset,
        colorDataType
      )
      setInterpolators(nextInterpolators)

      //set first interpolator
      const nextInterpolator = nextInterpolators[0]
      setInterpolator(nextInterpolator)

      //user values
      const nextUserValues = getDefaultUserValues(
        nextInterpolator,
        nextScaleType,
      )
      setUserValues(nextUserValues)
      const valuesForFinalScale = getUserValuesForFinalScale(nextUserValues)

      //notify ui
      const outScaleParams = {
        scaleType: nextScaleType,
        interpolator: nextInterpolator,
        userScaleValues: valuesForFinalScale,
        defaultOpacity,
        defaultColor,
        locked,
        __loaded: true,
      }
      onChange(outScaleParams)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [colorDataType, colorDataset, getDefaultUserValues, getUserValuesForFinalScale, defaultOpacity, defaultColor, onChange, locked]
  )

  const handleSetInterpolator = useCallback(
    (nextInterpolator, customUserValues) => {
      setInterpolator(nextInterpolator)

      //user values
      const nextUserValues = getDefaultUserValues(nextInterpolator, scaleType)
      setUserValues(nextUserValues)
      let valuesForFinalScale = getUserValuesForFinalScale(nextUserValues)
      
      if(customUserValues){
        const byDomain = keyBy(customUserValues, 'domain')
        valuesForFinalScale = valuesForFinalScale.map(v => ({
          ...v,
          range: byDomain[v.domain.toString()] ? byDomain[v.domain.toString()].userRange : v.range,
          opacity:
            byDomain[v.domain.toString()] &&
            Number.isFinite(Number(byDomain[v.domain.toString()].userOpacity))
              ? Number(byDomain[v.domain.toString()].userOpacity)
              : v.opacity
        }))
      }
      //notify ui
      const outScaleParams = {
        scaleType,
        interpolator: nextInterpolator,
        userScaleValues: valuesForFinalScale,
        defaultOpacity,
        defaultColor,
        locked,
        __loaded: true,
      }
      onChange(outScaleParams)
    },
    [getDefaultUserValues, getUserValuesForFinalScale, onChange, scaleType, defaultOpacity, defaultColor, locked,]
  )

  const handleChangeLocked = useCallback(
    (nextLocked) => {
      setLocked(nextLocked)
      //this is needed for disabiling automatic scale reset
      initialValue.current = true

      const outScaleParams = {
        scaleType,
        interpolator,
        userScaleValues: userValues,
        defaultOpacity,
        defaultColor,
        locked: nextLocked,
        __loaded: true,
      }
      onChange(outScaleParams)
    },
    [scaleType, interpolator, userValues, defaultOpacity, defaultColor, onChange]
  )



  const resetScale = useCallback(() => {
    handleSetInterpolator(interpolator, userValues)
  }, [handleSetInterpolator, interpolator, userValues])

  const invertScale = useCallback(() => {

    let reversedValues = [...userValues]
    reversedValues.reverse()

    const invertedValues = userValues.map((v, i) => ({
      ...v,
      userRange: reversedValues[i].userRange,
      range: reversedValues[i].range,

    }))

    setUserValues(invertedValues)
    handleChangeValues(invertedValues)
  }, [handleChangeValues, userValues])



  const prevMappingValue = usePrevious(mappingValue)

  useEffect(() => {
    if (prevMappingValue && mappingValue !== prevMappingValue) {
      initialValue.current = false
    }
  }, [mappingValue, prevMappingValue])

  useEffect(() => {
    if (!initialValue.current && !locked) {
      const nextScaleType = availableScaleTypes[0]
      handleChangeScaleType(nextScaleType)
    }

  }, [availableScaleTypes, handleChangeScaleType, locked])

  useEffect(() => {
    const nextInterpolators = getCompatibleInterpolators(
      scaleType,
      colorDataset,
      colorDataType
    )
    setInterpolators(nextInterpolators)

    if (
      scaleType &&
      nextInterpolators.length > 0 &&
      (!interpolator || !nextInterpolators.includes(interpolator))
    ) {
      handleSetInterpolator(nextInterpolators[0])
    }
  }, [
    colorDataType,
    colorDataset,
    handleSetInterpolator,
    interpolator,
    scaleType,
  ])

  // update scale on dataset update.
  // #TODO: fixme

  // const prevDataset = usePrevious(colorDataset)
  // const prevScaleType = usePrevious(colorDataset)

  // useEffect(() => {
  //   if (!locked && colorDataset !== prevDataset && prevScaleType === scaleType) {
  //     console.info("dddd resetting scope")
  //     const nextUserValues = getDefaultUserValues(
  //       interpolator,
  //       scaleType,
  //     )
  //     setUserValues(nextUserValues)
  //     const valuesForFinalScale = getUserValuesForFinalScale(nextUserValues)
  //     //notify ui
  //     const outScaleParams = {
  //       scaleType,
  //       interpolator,
  //       userScaleValues: valuesForFinalScale,
  //       defaultColor,
  //       locked,
  //     }
  //     onChange(outScaleParams)
  //   }
  // }, [colorDataset, defaultColor, getDefaultUserValues, getUserValuesForFinalScale, interpolator, locked, onChange, prevDataset, prevScaleType, scaleType])

  const mobileVisibleCount = 6
  const visibleUserValues =
    isMobileLayout && scaleType === 'ordinal' && !showAllCategories
      ? userValues.slice(0, mobileVisibleCount)
      : userValues
  const hiddenCategoryCount =
    isMobileLayout && scaleType === 'ordinal'
      ? Math.max(0, userValues.length - mobileVisibleCount)
      : 0


  return (
    <>
      {!hasAnyMapping && (
        <div className={styles['color-scale-empty-state']}>
          <strong>Map a field to Color</strong>
          <span>
            Go back to the Mapping step and map a field to Color to enable data color controls.
          </span>
        </div>
      )}
      {hasAnyMapping && (
        <>
      <Row className={props.className} style={{marginTop:'8px', marginBottom:'8px'}}>
        <Col xs={12}>
          <div className={styles['scale-mode-label']}>Color mode</div>
          <div className={styles['scale-mode-grid']}>
            {availableScaleTypes.map((s) => {
              const meta = SCALE_MODE_META[s] || {
                label: s,
                description: '',
              }
              return (
                <button
                  key={s}
                  type="button"
                  className={[
                    styles['scale-mode-card'],
                    scaleType === s ? styles['scale-mode-card-active'] : '',
                  ].join(' ')}
                  onClick={() => handleChangeScaleType(s)}
                >
                  <strong>{meta.label}</strong>
                  <span>{meta.description}</span>
                </button>
              )
            })}
          </div>
        </Col>
      </Row>

      {/* Color scheme */}
      <Row className={[props.className].join(' ')} style={{marginTop:'8px', marginBottom:'8px'}}>
        <Col xs={5} className="d-flex align-items-center nowrap">
          Color scheme
        </Col>
        <Col xs={7}>
          <ColorSchemesDropDown
            interpolators={interpolators}
            interpolator={interpolator}
            setInterpolator={handleSetInterpolator}
            // To display color-scale preview
            colorDataset={colorDataset}
            colorDataType={colorDataType}
            scaleType={scaleType}
            currentFinalScale={currentFinalScale}
          />
          {scaleType === 'ordinal' && interpolators.length === 1 && (
            <div className="small text-muted mt-1">
              Only one category is present, so a single safe color scheme is available.
            </div>
          )}
        </Col>
      </Row>

      {/* Scale color swatches */}
      {colorDataType && userValues && (
        <div className={styles['color-swatches-list']}>
          <div className={styles['color-scale-section-heading']}>Global opacity</div>
          <Row
            className={[
              styles['chart-option'],
              styles['color-swatch'],
              styles['ordinal'],
              isMobileLayout ? styles['mobile-color-swatch'] : '',
            ].join(' ')}
          >
            <Col xs={12}>
              <div className={styles['color-scale-item']}>
                <span className={styles['color-scale-mobile-leading-label']}>All items</span>
                <div className={styles['color-scale-opacity-control']}>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.05"
                    value={defaultOpacity}
                    onChange={(e) => handleChangeDefaultOpacity(e.target.value)}
                  />
                  <span>{Math.round(defaultOpacity * 100)}%</span>
                </div>
              </div>
            </Col>
          </Row>
          {isMobileLayout && scaleType === 'ordinal' && (
            <div className={styles['color-scale-section-help']}>
              Sets the base. Per-category overrides inherit this by default.
            </div>
          )}
          {scaleType === 'ordinal' && (
            <div className={styles['color-scale-section-row']}>
              <div className={styles['color-scale-section-heading']}>Per-category</div>
              <div className={styles['color-scale-section-actions']}>
                <ResetBtn resetScale={resetScale} />
              </div>
            </div>
          )}
          {visibleUserValues.map((userValue, i) => (
            <Row
              key={i}
              className={[
                styles['chart-option'],
                styles['color-swatch'],
                scaleType !== 'ordinal'
                  ? styles['not-ordinal']
                  : styles['ordinal'],
                isMobileLayout ? styles['mobile-color-swatch'] : '',
              ].join(' ')}
            >
              <Col xs={12}>
                <div className={styles['color-scale-item']}>
                  {scaleType === 'ordinal' &&
                    get(userValue, 'domain') !== undefined && (
                      <div className={styles['color-scale-mobile-item']}>
                        <div className={styles['color-scale-mobile-row']}>
                          <div className={styles['color-scale-mobile-name-group']}>
                            <div className={styles['color-scale-mobile-picker']}>
                              <InilineColorPicker
                                compact
                                color={userValue.userRange}
                                onChange={(color) => {
                                  setUserValueRange(i, color)
                                }}
                              />
                            </div>
                            <span
                              className={styles['color-scale-mobile-name']}
                              title={userValue.domain && userValue.domain.toString()}
                            >
                              {userValue.domain === ''
                                ? '[empty string]'
                                : userValue.domain.toString()}
                            </span>
                          </div>
                          <div className={styles['color-scale-mobile-meta']}>
                            <span className={styles['color-scale-mobile-hex']}>
                              {userValue.userRange}
                            </span>
                          </div>
                        </div>
                        <div className={styles['color-scale-mobile-opacity-row']}>
                          <span className={styles['color-scale-mobile-opacity-label']}>
                            Opacity
                          </span>
                          <div className={styles['color-scale-opacity-control']}>
                            <input
                              type="range"
                              min="0.1"
                              max="1"
                              step="0.05"
                              value={userValue.userOpacity ?? defaultOpacity}
                              onChange={(e) => setUserValueOpacity(i, e.target.value)}
                            />
                            <span>
                              {Math.round(
                                Number(userValue.userOpacity ?? defaultOpacity) * 100
                              )}%
                            </span>
                            {userValue.userOpacity !== undefined && (
                              <button
                                type="button"
                                className={styles['color-scale-opacity-reset']}
                                onClick={() => clearUserValueOpacityOverride(i)}
                                title="Reset to All items opacity"
                                aria-label="Reset to All items opacity"
                              >
                                ↺
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  {scaleType !== 'ordinal' && (
                    <>
                      <span className="nowrap">
                        {i === 0
                          ? 'Start'
                          : i === userValues.length - 1
                            ? 'End'
                            : 'Middle'}
                      </span>
                      <input
                        disabled={locked}
                        type={getValueType(userValue.userDomain)}
                        className="form-control text-field"
                        value={getDatePickerValue(userValue)}
                        onChange={(e) => {
                          if (colorDataType === 'date') {
                            setUserValueDomain(i, new Date(e.target.value))
                          } else {
                            setUserValueDomain(i, e.target.value)
                          }
                        }}
                      ></input>
                    </>
                  )}
                  {scaleType !== 'ordinal' && (
                    <>
                      <InilineColorPicker
                        color={userValue.userRange}
                        onChange={(color) => {
                          setUserValueRange(i, color)
                        }}
                      />
                      <div className={styles['color-scale-opacity-control']}>
                        <input
                          type="range"
                          min="0.1"
                          max="1"
                          step="0.05"
                          value={userValue.userOpacity ?? defaultOpacity}
                          onChange={(e) => setUserValueOpacity(i, e.target.value)}
                        />
                        <span>
                          {Math.round(Number(userValue.userOpacity ?? defaultOpacity) * 100)}%
                        </span>
                        {userValue.userOpacity !== undefined && (
                          <button
                            type="button"
                            className={styles['color-scale-opacity-reset']}
                            onClick={() => clearUserValueOpacityOverride(i)}
                            title="Reset to All items opacity"
                            aria-label="Reset to All items opacity"
                          >
                            ↺
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </Col>
            </Row>
          ))}
          {hiddenCategoryCount > 0 && !showAllCategories && (
            <div className={styles['color-scale-show-more-wrap']}>
              <button
                type="button"
                className={styles['color-scale-show-more']}
                onClick={() => setShowAllCategories(true)}
              >
                Show {hiddenCategoryCount} more categories
              </button>
            </div>
          )}
          <Row>
            <Col className="d-flex justify-content-end">
              {scaleType !== 'ordinal' && <ResetBtn resetScale={resetScale} />}
              <InvertBtn invertScale={invertScale} />
              {
                scaleType !== 'ordinal' && (
                  <LockBtn locked={locked} handleChangeLocked={handleChangeLocked} />
                )
              }

            </Col>
          </Row>

        </div>
      )}
        </>
      )}
    </>
  )
}

export default ChartOptionColorScale
